/**
 * Service for handling locations API calls
 * Fetches office locations from the backend API
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetch all locations from the API
 * @returns {Promise<Object>} Response object with success status and locations data
 */
export const getLocations = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/locations`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.status === 'success' && data.data) {
      // Transform the API data into a format suitable for the frontend
      const locations = data.data.map(location => ({
        id: location.id,
        name: location.office_name,
        shortName: location.short_name,
        address: location.address,
        phone: location.phone_number,
        email: location.email,
        whatsapp: location.whatsapp_number,
        mapIframe: location.map_iframe,
        status: location.status,
      }));

      return {
        success: true,
        locations,
        count: data.count,
      };
    } else {
      return {
        success: false,
        error: 'Invalid response format',
        locations: [],
      };
    }
  } catch (error) {
    console.error('Error fetching locations:', error);
    return {
      success: false,
      error: error.message,
      locations: [],
    };
  }
};

/**
 * Get location name by ID
 * @param {Array} locations - Array of location objects
 * @param {number} locationId - The location ID to find
 * @returns {string} The office name or default fallback
 */
export const getLocationNameById = (locations, locationId) => {
  if (!locations || !Array.isArray(locations)) {
    return 'Location not specified';
  }
  
  const location = locations.find(loc => loc.id === parseInt(locationId));
  return location ? location.name : 'Location not found';
};

/**
 * Get all location names as an array (for dropdown/filter usage)
 * @param {Array} locations - Array of location objects
 * @returns {Array} Array of location names
 */
export const getLocationNames = (locations) => {
  if (!locations || !Array.isArray(locations)) {
    return [];
  }
  
  return locations.map(location => location.name);
};

/**
 * Get location details by ID
 * @param {Array} locations - Array of location objects
 * @param {number} locationId - The location ID to find
 * @returns {Object|null} The complete location object or null if not found
 */
export const getLocationById = (locations, locationId) => {
  if (!locations || !Array.isArray(locations)) {
    return null;
  }
  
  return locations.find(loc => loc.id === parseInt(locationId)) || null;
};
